import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Youtube,
  Clock,
  FileText,
  Scale,
  Users,
  Building2,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  NavigationItem,
  SocialLink,
  FooterConfig,
} from '../pages/admin/Configs/utils/types';

interface SiteConfig {
  site_name: string;
  site_subtitle: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  office_hours: string;
  social_facebook?: string;
  social_instagram?: string;
  social_youtube?: string;
}

export function Footer() {
  const [footerConfig, setFooterConfig] = useState<FooterConfig | null>(null);
  const [navItems, setNavItems] = useState<NavigationItem[]>([]);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadFooterData();
  }, []);

  const loadFooterData = async () => {
    try {
      setLoading(true);
      // Load site config data
      const { data: configData, error: configError } = await supabase
        .from('site_configs')
        .select('*')
        .single();

      if (configError) throw configError;
      setSiteConfig(configData);

      // Load footer configuration
      const { data: footerData, error: footerError } = await supabase
        .from('footer_configs')
        .select('*')
        .single();

      if (footerError) throw footerError;
      setFooterConfig(footerData || getDefaultFooterConfig());

      // Load navigation items
      const { data: navItemsData, error: navItemsError } = await supabase
        .from('navigation_items')
        .select('*')
        .eq('type', 'footer')
        .eq('active', true)
        .order('order', { ascending: true });

      if (navItemsError) throw navItemsError;
      setNavItems(navItemsData || []);

      // Load social links
      const { data: socialData, error: socialError } = await supabase
        .from('social_links')
        .select('*')
        .eq('active', true)
        .order('order', { ascending: true });

      if (socialError) throw socialError;
      setSocialLinks(socialData || []);
    } catch (error) {
      console.error('Error loading footer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFooterConfig = (): FooterConfig => {
    return {
      show_logo: true,
      logo_url: 'https://erosis.com.br/uploads/sp.png',
      show_about: true,
      about_text: 'Breve descrição da história da cidade',
      show_contact: true,
      show_social: true,
      show_links: true,
      background_color: '#11515B',
      background_dark_color: '#1e293b',
      text_color: '#ffffff',
      text_dark_color: '#f1f5f9',
      copyright_text: `© ${currentYear} Prefeitura Municipal. Todos os direitos reservados.`,
    };
  };

  // Function to process copyright text, replacing {ano} with current year
  const processCopyrightText = (text: string) => {
    return text?.replace('{ano}', currentYear.toString()) || '';
  };

  if (loading) {
    return (
      <footer className="relative bg-gradient-to-br from-[#11515B] to-[#0A323A] dark:bg-gray-800 text-white overflow-hidden">
        <div className="dark:bg-gray-900 min-h-[300px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
        </div>
      </footer>
    );
  }

  return (
    <footer
      className="relative bg-gradient-to-br from-[#11515B] to-[#0A323A] dark:bg-gray-800 text-white overflow-hidden"
      style={{
        background: footerConfig?.background_color
          ? `linear-gradient(to bottom right, ${
              footerConfig.background_color
            }, ${adjustColor(footerConfig.background_color, -20)})`
          : undefined,
        color: footerConfig?.text_color || '#ffffff',
      }}
    >
      <div className="dark:bg-gray-900">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-1/4 -top-1/4 w-1/2 h-1/2 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute -left-1/4 -bottom-1/4 w-1/2 h-1/2 bg-white/5 rounded-full blur-3xl"></div>
        </div>

        {/* Wave Separator */}
        <div className="absolute top-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 100"
            className="fill-gray-400 w-full h-auto transform translate-y-[-98%]"
            preserveAspectRatio="none"
          >
            <path d="M0,50 C280,100 720,0 1440,50 L1440,100 L0,100 Z"></path>
          </svg>
        </div>

        <div className="relative container mx-auto px-4 pt-20 pb-12">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* About Section */}
            {footerConfig?.show_about && (
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  {footerConfig.show_logo && footerConfig.logo_url && (
                    <img
                      src={footerConfig.logo_url}
                      alt="Logo"
                      className="h-10 w-auto dark:brightness-110"
                    />
                  )}
                  <h3 className="text-xl font-bold">Prefeitura Municipal</h3>
                </div>
                <p className="text-white/80 leading-relaxed">
                  {footerConfig.about_text || ''}
                </p>
                {footerConfig?.show_social && (
                  <div className="flex space-x-4">
                    {socialLinks.length > 0 ? (
                      socialLinks.map((link) => (
                        <a
                          key={link.id}
                          href={link.url}
                          target="_blank"
                          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                          aria-label={link.platform}
                        >
                          {link.platform === 'facebook' && (
                            <Facebook className="w-5 h-5" />
                          )}
                          {link.platform === 'instagram' && (
                            <Instagram className="w-5 h-5" />
                          )}
                          {link.platform === 'youtube' && (
                            <Youtube className="w-5 h-5" />
                          )}
                        </a>
                      ))
                    ) : (
                      <>
                        {siteConfig?.social_facebook && (
                          <a
                            href={siteConfig.social_facebook}
                            target="_blank"
                            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                            aria-label="Facebook"
                          >
                            <Facebook className="w-5 h-5" />
                          </a>
                        )}
                        {siteConfig?.social_instagram && (
                          <a
                            href={siteConfig.social_instagram}
                            target="_blank"
                            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                            aria-label="Instagram"
                          >
                            <Instagram className="w-5 h-5" />
                          </a>
                        )}
                        {siteConfig?.social_youtube && (
                          <a
                            href={siteConfig.social_youtube}
                            target="_blank"
                            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                            aria-label="YouTube"
                          >
                            <Youtube className="w-5 h-5" />
                          </a>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Quick Links */}
            {footerConfig?.show_links && (
              <div>
                <h3 className="text-lg font-semibold mb-6 flex items-center">
                  Links Rápidos
                </h3>
                <ul className="space-y-3">
                  {navItems.length > 0
                    ? navItems.map((link) => (
                        <li key={link.id}>
                          <Link
                            to={link.url}
                            className="group flex items-center text-white/80 hover:text-white transition-colors"
                            target={link.is_external ? '_blank' : undefined}
                            rel={
                              link.is_external
                                ? 'noopener noreferrer'
                                : undefined
                            }
                          >
                            <ChevronRight className="w-4 h-4 mr-2 transform group-hover:translate-x-1 transition-transform" />
                            {link.label}
                            {link.is_external && (
                              <ExternalLink className="w-3 h-3 ml-1 opacity-50" />
                            )}
                          </Link>
                        </li>
                      ))
                    : [
                        { label: 'Painel Administrativo', href: '/admin' },
                        { label: 'Notícias', href: '/noticias' },
                        { label: 'Licitações', href: '/licitacoes' },
                        { label: 'Concursos', href: '/concursos' },
                        { label: 'Legislação', href: '/documentos/legislacao' },
                        {
                          label: 'Webmail',
                          href: 'https://outlook.office.com/',
                        },
                      ].map((link, index) => (
                        <li key={index}>
                          <Link
                            to={link.href}
                            className="group flex items-center text-white/80 hover:text-white transition-colors"
                          >
                            <ChevronRight className="w-4 h-4 mr-2 transform group-hover:translate-x-1 transition-transform" />
                            {link.label}
                            {link.href.startsWith('http') && (
                              <ExternalLink className="w-3 h-3 ml-1 opacity-50" />
                            )}
                          </Link>
                        </li>
                      ))}
                </ul>
              </div>
            )}

            {/* Contact Info */}
            {footerConfig?.show_contact && (
              <div>
                <h3 className="text-lg font-semibold mb-6 flex items-center">
                  Contato
                </h3>
                <ul className="space-y-4">
                  {siteConfig?.contact_address && (
                    <li className="flex items-center">
                      <MapPin className="w-5 h-5 mr-3 text-white/70" />
                      <span className="text-white/80">
                        {siteConfig.contact_address}
                      </span>
                    </li>
                  )}
                  {siteConfig?.contact_phone && (
                    <li className="flex items-center">
                      <Phone className="w-5 h-5 mr-3 text-white/70" />
                      <a className="text-white/80 hover:text-white transition-colors">
                        {siteConfig.contact_phone}
                      </a>
                    </li>
                  )}
                  {siteConfig?.contact_email && (
                    <li className="flex items-center">
                      <Mail className="w-5 h-5 mr-3 text-white/70" />
                      <a className="text-white/80 hover:text-white transition-colors">
                        {siteConfig.contact_email}
                      </a>
                    </li>
                  )}
                  {siteConfig?.office_hours && (
                    <li className="flex items-center">
                      <Clock className="w-5 h-5 mr-3 text-white/70" />
                      <span className="text-white/80">
                        {siteConfig.office_hours}
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Services */}
            <div>
              <h3 className="text-lg font-semibold mb-6 flex items-center">
                Serviços
              </h3>
              <div className="space-y-4">
                <Link
                  to="/servicos"
                  className="block p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <h4 className="font-medium mb-1">Serviços ao Cidadão</h4>
                  <p className="text-sm text-white/70">
                    Acesse todos os serviços disponíveis para a população
                  </p>
                </Link>
                <Link
                  to="/contato"
                  className="block p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <h4 className="font-medium mb-1">Fale Conosco</h4>
                  <p className="text-sm text-white/70">
                    Entre em contato com a Prefeitura Municipal
                  </p>
                </Link>
                <a
                  href="/servico/transparencia"
                  target="_blank"
                  className="block p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <h4 className="font-medium mb-1 flex items-center">
                    Transparência
                    <ExternalLink className="w-4 h-4 ml-1 opacity-50" />
                  </h4>
                  <p className="text-sm text-white/70">
                    Acesse o Portal da Transparência
                  </p>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-white/60 text-sm">
                {footerConfig?.copyright_text
                  ? ` © ${currentYear} ${processCopyrightText(
                      footerConfig.copyright_text
                    )} `
                  : `© ${currentYear} Prefeitura Municipal. Todos os direitos reservados.`}
              </div>
              <div className="flex items-center space-x-6 text-sm text-white/60">
                <Link
                  to="/privacidade"
                  className="hover:text-white transition-colors"
                >
                  Política de Privacidade
                </Link>
                <Link
                  to="/acessibilidade"
                  className="hover:text-white transition-colors"
                >
                  Acessibilidade
                </Link>
                <Link
                  to="/mapa-do-site"
                  className="hover:text-white transition-colors"
                >
                  Mapa do Site
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx="true">{`
      .dark footer {
        background: ${
          footerConfig?.background_dark_color || '#1e293b'
        } !important;
        color: ${footerConfig?.text_dark_color || '#f1f5f9'} !important;
      }
    `}</style>
    </footer>
  );
}

// Helper function to darken or lighten a color
function adjustColor(hex: string, percent: number): string {
  // Remove # if it exists
  hex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Adjust each color component
  const adjustR = Math.floor((r * (100 + percent)) / 100);
  const adjustG = Math.floor((g * (100 + percent)) / 100);
  const adjustB = Math.floor((b * (100 + percent)) / 100);

  // Ensure values are within 0-255 range
  const newR = adjustR > 255 ? 255 : adjustR < 0 ? 0 : adjustR;
  const newG = adjustG > 255 ? 255 : adjustG < 0 ? 0 : adjustG;
  const newB = adjustB > 255 ? 255 : adjustB < 0 ? 0 : adjustB;

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG
    .toString(16)
    .padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}
